import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import api from '../utils/api';

type PomodoroMode = 'focus' | 'break';

export type PomodoroProfileV1 = {
  focusMinutes: number;
  breakMinutes: number;
  disabled?: boolean; // true 表示该范围不启用番茄
};

export type PomodoroSettingsV1 = {
  version: 1;
  focusMinutes: number; // default 25
  breakMinutes: number; // default 5
  autoFullscreen: boolean; // default false（由 Today/Focus UI 决定是否自动进入专注屏）
  soundEnabled: boolean; // default false（先占位）
  volume: number; // 0..1
};

export type PomodoroSettingsV2 = {
  version: 2;
  // 全局默认
  focusMinutes: number; // default 25
  breakMinutes: number; // default 5
  autoFullscreen: boolean;
  soundEnabled: boolean;
  volume: number;
  // 偏好：task > guide > global
  profiles: {
    byTaskId: Record<string, PomodoroProfileV1>;
    byGuideSlug: Record<string, PomodoroProfileV1>;
  };
};

export type PomodoroSettings = PomodoroSettingsV2;

export type PomodoroBoundTask = {
  id: string;
  title: string;
  guideSlug?: string | null;
} | null;

type EffectivePomodoroProfile = {
  focusMinutes: number;
  breakMinutes: number;
  enabled: boolean;
  source: 'task' | 'guide' | 'global';
  sourceKey: string | null;
};

type PomodoroContextValue = {
  // state
  settings: PomodoroSettings;
  effective: EffectivePomodoroProfile;
  mode: PomodoroMode;
  running: boolean;
  sessionActive: boolean; // 曾经开始过（暂停也算），用于 mini 显示/恢复
  remainingSec: number;
  todayPomodoros: number;
  boundTask: PomodoroBoundTask;

  showSettings: boolean;
  focusScreen: boolean;
  focusShowGuide: boolean;

  // actions
  setSettings: React.Dispatch<React.SetStateAction<PomodoroSettings>>;
  setShowSettings: (v: boolean) => void;
  setFocusScreen: (v: boolean) => void;
  setFocusShowGuide: (v: boolean) => void;

  bindToTask: (t: PomodoroBoundTask) => void;
  toggleRunning: () => void;
  reset: (mode?: PomodoroMode) => void;
  completeOrSkipBreak: () => void;
};

const PomodoroContext = createContext<PomodoroContextValue | null>(null);

const POMODORO_SETTINGS_KEY = 'personal_blog.today.pomodoro.settings.v1';
const POMODORO_COUNTS_KEY = 'personal_blog.today.pomodoro.counts.v1';
const DAY_CUTOFF_HOUR = 4;
const DEVICE_ID_KEY = 'personal_blog.frontend.device_id.v1';

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function safeJsonParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function getOrCreateDeviceId() {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c: any = globalThis as any;
  const id = c?.crypto?.randomUUID ? c.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  try {
    localStorage.setItem(DEVICE_ID_KEY, id);
  } catch {
    // ignore
  }
  return id;
}

function normalizeProfile(p: any): PomodoroProfileV1 {
  return {
    focusMinutes: clamp(Number(p?.focusMinutes ?? 25), 1, 180),
    breakMinutes: clamp(Number(p?.breakMinutes ?? 5), 1, 60),
    disabled: !!p?.disabled,
  };
}

function normalizeSettingsV2(s: any): PomodoroSettingsV2 {
  const byTaskIdRaw = (s?.profiles?.byTaskId ?? {}) as Record<string, any>;
  const byGuideSlugRaw = (s?.profiles?.byGuideSlug ?? {}) as Record<string, any>;
  const byTaskId: Record<string, PomodoroProfileV1> = {};
  const byGuideSlug: Record<string, PomodoroProfileV1> = {};

  for (const [k, v] of Object.entries(byTaskIdRaw)) {
    if (!k) continue;
    byTaskId[k] = normalizeProfile(v);
  }
  for (const [k, v] of Object.entries(byGuideSlugRaw)) {
    if (!k) continue;
    byGuideSlug[k] = normalizeProfile(v);
  }

  return {
    version: 2,
    focusMinutes: clamp(Number(s?.focusMinutes ?? 25), 1, 180),
    breakMinutes: clamp(Number(s?.breakMinutes ?? 5), 1, 60),
    autoFullscreen: !!s?.autoFullscreen,
    soundEnabled: s?.soundEnabled == null ? true : !!s?.soundEnabled, // 新默认：开启
    volume: clamp(Number(s?.volume ?? 0.35), 0, 1),
    profiles: { byTaskId, byGuideSlug },
  };
}

function loadPomodoroSettings(): PomodoroSettingsV2 {
  const parsed = safeJsonParse<any>(localStorage.getItem(POMODORO_SETTINGS_KEY));
  if (parsed && parsed.version === 2) return normalizeSettingsV2(parsed);
  if (parsed && parsed.version === 1) {
    // v1 -> v2 迁移：保留全局设置，profiles 置空
    return normalizeSettingsV2({
      version: 2,
      focusMinutes: parsed.focusMinutes,
      breakMinutes: parsed.breakMinutes,
      autoFullscreen: parsed.autoFullscreen,
      soundEnabled: parsed.soundEnabled,
      volume: parsed.volume,
      profiles: { byTaskId: {}, byGuideSlug: {} },
    });
  }
  return normalizeSettingsV2({
    version: 2,
    focusMinutes: 25,
    breakMinutes: 5,
    autoFullscreen: false,
    soundEnabled: true,
    volume: 0.35,
    profiles: { byTaskId: {}, byGuideSlug: {} },
  });
}

function savePomodoroSettings(s: PomodoroSettingsV2) {
  try {
    localStorage.setItem(POMODORO_SETTINGS_KEY, JSON.stringify(s));
  } catch {
    // ignore
  }
}

function loadPomodoroCounts(): Record<string, number> {
  return safeJsonParse<Record<string, number>>(localStorage.getItem(POMODORO_COUNTS_KEY)) ?? {};
}

function savePomodoroCounts(v: Record<string, number>) {
  try {
    localStorage.setItem(POMODORO_COUNTS_KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
}

function getPomodoroDayKey(now = new Date()) {
  const d = new Date(now);
  if (d.getHours() < DAY_CUTOFF_HOUR) d.setDate(d.getDate() - 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export const PomodoroProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authTick, setAuthTick] = useState(0);
  useEffect(() => {
    const handler = () => setAuthTick((x) => x + 1);
    window.addEventListener('today-auth-changed', handler as any);
    window.addEventListener('storage', handler as any);
    return () => {
      window.removeEventListener('today-auth-changed', handler as any);
      window.removeEventListener('storage', handler as any);
    };
  }, []);

  const isLoggedIn = useMemo(() => !!localStorage.getItem('today_token'), [authTick]);

  const [settings, setSettings] = useState<PomodoroSettingsV2>(() => loadPomodoroSettings());
  const [mode, setMode] = useState<PomodoroMode>('focus');
  const [running, setRunning] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [remainingSec, setRemainingSec] = useState(() => loadPomodoroSettings().focusMinutes * 60);
  const remainingSecRef = useRef<number>(remainingSec);
  useEffect(() => {
    remainingSecRef.current = remainingSec;
  }, [remainingSec]);

  const [showSettings, setShowSettings] = useState(false);
  const [focusScreen, setFocusScreen] = useState(false);
  const [focusShowGuide, setFocusShowGuide] = useState(false);

  const [counts, setCounts] = useState<Record<string, number>>(() => loadPomodoroCounts());
  const [boundTask, setBoundTask] = useState<PomodoroBoundTask>(null);
  const boundTaskIdRef = useRef<string | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioUnlockedRef = useRef(false);
  const cloudUpdatedAtRef = useRef<string | null>(null);
  const pushTimerRef = useRef<number | null>(null);
  const deviceIdRef = useRef<string>(getOrCreateDeviceId());
  const sessionIdRef = useRef<string | null>(null);
  const heartbeatTimerRef = useRef<number | null>(null);

  // 退出登录：清理会话与计时（避免切用户/登出后带着旧会话）
  useEffect(() => {
    if (isLoggedIn) return;
    sessionIdRef.current = null;
    if (heartbeatTimerRef.current) window.clearInterval(heartbeatTimerRef.current);
    heartbeatTimerRef.current = null;
    setRunning(false);
    setSessionActive(false);
    setFocusScreen(false);
  }, [isLoggedIn]);

  const dayKey = useMemo(() => getPomodoroDayKey(), [counts]); // counts 更新时顺带刷新（足够用）
  const todayPomodoros = counts[dayKey] ?? 0;

  const resolveEffective = (task: PomodoroBoundTask, s = settings): EffectivePomodoroProfile => {
    const tid = task?.id ?? null;
    const slug = (task?.guideSlug ?? '').trim();

    const fromTask = tid ? s.profiles.byTaskId[tid] : undefined;
    if (fromTask) {
      return {
        focusMinutes: fromTask.focusMinutes,
        breakMinutes: fromTask.breakMinutes,
        enabled: !fromTask.disabled,
        source: 'task',
        sourceKey: tid,
      };
    }

    const fromGuide = slug ? s.profiles.byGuideSlug[slug] : undefined;
    if (fromGuide) {
      return {
        focusMinutes: fromGuide.focusMinutes,
        breakMinutes: fromGuide.breakMinutes,
        enabled: !fromGuide.disabled,
        source: 'guide',
        sourceKey: slug,
      };
    }

    return { focusMinutes: s.focusMinutes, breakMinutes: s.breakMinutes, enabled: true, source: 'global', sourceKey: null };
  };

  const effective = useMemo(() => resolveEffective(boundTask, settings), [boundTask?.id, boundTask?.guideSlug, settings]);

  // 登录后：以服务器为主（server-first）拉取 settings
  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    const pull = async () => {
      try {
        const res = await api.get<{ data: any; updatedAt: string | null }>('/pomodoro-settings');
        if (cancelled) return;
        if (res.data?.data) {
          const normalized = normalizeSettingsV2(res.data.data);
          cloudUpdatedAtRef.current = res.data.updatedAt ?? null;
          setSettings(normalized);
          try {
            localStorage.setItem(POMODORO_SETTINGS_KEY, JSON.stringify(normalized));
          } catch {
            // ignore
          }
        }
      } catch (e: any) {
        // 404：后端未升级；其它错误（401/网络）不缓存
        if (e?.response?.status === 404) return;
      }
    };
    void pull();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const ensureAudioUnlocked = async () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx: any = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current!;
      if (ctx.state !== 'running') await ctx.resume();
      audioUnlockedRef.current = ctx.state === 'running';
    } catch {
      // ignore
    }
  };

  const playBeep = async (kind: 'start' | 'focus_end' | 'break_end') => {
    if (!settings.soundEnabled) return;
    if (!audioUnlockedRef.current) return;
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const vol = clamp(Number(settings.volume ?? 0.35), 0, 1);
    const now = ctx.currentTime;
    const mk = (freq: number, startAt: number, dur: number, gain: number) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startAt);
      g.gain.setValueAtTime(0.0001, startAt);
      g.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain * vol), startAt + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(startAt);
      osc.stop(startAt + dur + 0.02);
    };

    if (kind === 'start') {
      mk(880, now + 0.0, 0.11, 0.25);
      return;
    }
    if (kind === 'focus_end') {
      mk(660, now + 0.0, 0.10, 0.22);
      mk(990, now + 0.14, 0.12, 0.25);
      return;
    }
    // break_end
    mk(520, now + 0.0, 0.10, 0.22);
    mk(780, now + 0.14, 0.10, 0.22);
  };

  const resetAfterStolen = () => {
    // 被抢占：停止并重置到“当前绑定任务”的有效专注时长，进入暂停态
    setRunning(false);
    setSessionActive(false);
    setFocusScreen(false);
    setMode('focus');
    const eff = resolveEffective(boundTask, settings);
    setRemainingSec(eff.focusMinutes * 60);
  };

  // stop：reset 或解绑/切换任务时尝试释放会话
  const stopCloudSession = async () => {
    if (!isLoggedIn) return;
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    const deviceId = typeof deviceIdRef.current === 'string' ? deviceIdRef.current : getOrCreateDeviceId();
    try {
      await api.post('/pomodoro-session/stop', { deviceId, sessionId });
    } catch {
      // ignore
    } finally {
      sessionIdRef.current = null;
    }
  };

  const reset = (m: PomodoroMode = 'focus') => {
    setMode(m);
    setRunning(false);
    setSessionActive(false);
    const eff = resolveEffective(boundTask, settings);
    const secs = (m === 'focus' ? eff.focusMinutes : eff.breakMinutes) * 60;
    setRemainingSec(secs);
    void stopCloudSession();
  };

  const bindToTask = (t: PomodoroBoundTask) => {
    setBoundTask(t);
    const nextId = t?.id ?? null;
    const prevId = boundTaskIdRef.current;
    boundTaskIdRef.current = nextId;

    // 切换任务：直接停止（并重置为专注时长）
    if (prevId && nextId && prevId !== nextId) {
      const eff = resolveEffective(t, settings);
      setMode('focus');
      setRunning(false);
      setSessionActive(false);
      setRemainingSec(eff.focusMinutes * 60);
      setFocusShowGuide(false);
      setFocusScreen(false);
      void stopCloudSession();
    }
    if (!prevId && nextId) {
      // 从未绑定 -> 绑定：也重置一下，避免继承之前的剩余时间
      const eff = resolveEffective(t, settings);
      setMode('focus');
      setRunning(false);
      setSessionActive(false);
      setRemainingSec(eff.focusMinutes * 60);
      setFocusShowGuide(false);
      setFocusScreen(false);
      void stopCloudSession();
    }
  };

  const toggleRunning = () => {
    if (!boundTask) return;
    if (!resolveEffective(boundTask, settings).enabled) return;
    const next = !running;
    if (!next) {
      setRunning(false);
      setSessionActive(true);
      return;
    }
    // start: 申请云端唯一会话（登录用户）
    const startRun = async () => {
      // 用户手势触发：解锁音频（便于后续到点自动播放）
      await ensureAudioUnlocked();
      await playBeep('start');

      if (!isLoggedIn) {
        setRunning(true);
        setSessionActive(true);
        return;
      }

      const deviceId = typeof deviceIdRef.current === 'string' ? deviceIdRef.current : getOrCreateDeviceId();
      try {
        const res = await api.post<any>('/pomodoro-session/start', {
          deviceId,
          force: false,
          boundTaskId: boundTask.id,
          boundTaskTitle: boundTask.title,
          boundGuideSlug: boundTask.guideSlug ?? null,
          mode,
          remainingSec: remainingSecRef.current,
          running: true,
        });
        sessionIdRef.current = res.data?.sessionId ?? res.data?.active?.sessionId ?? null;
        setRunning(true);
        setSessionActive(true);
      } catch (e: any) {
        if (e?.response?.status === 409) {
          const ok = window.confirm('检测到另一台设备正在使用番茄时钟。是否抢占？');
          if (!ok) return;
          try {
            const res2 = await api.post<any>('/pomodoro-session/start', {
              deviceId,
              force: true,
              // 重要：抢占时默认“继续原会话状态”（后端会保留旧 remainingSec/mode/boundTask）
              running: true,
            });
            const active = res2.data?.active;
            sessionIdRef.current = res2.data?.sessionId ?? active?.sessionId ?? null;
            if (active?.boundTaskId && active?.boundTaskTitle) {
              setBoundTask({
                id: String(active.boundTaskId),
                title: String(active.boundTaskTitle),
                guideSlug: active?.boundGuideSlug != null ? String(active.boundGuideSlug) : null,
              });
            }
            if (active?.mode === 'focus' || active?.mode === 'break') setMode(active.mode);
            if (Number.isFinite(active?.remainingSec)) setRemainingSec(Math.max(0, Math.floor(Number(active.remainingSec))));
            setRunning(true);
            setSessionActive(true);
          } catch {
            // ignore
          }
          return;
        }
      }
    };

    void startRun();
  };

  // heartbeat：运行/暂停（sessionActive）期间维持会话；若被抢占则停止并提示
  useEffect(() => {
    if (!isLoggedIn) return;
    if (!(running || sessionActive)) return;
    if (!boundTask) return;
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;

    const deviceId = typeof deviceIdRef.current === 'string' ? deviceIdRef.current : getOrCreateDeviceId();
    const tick = async () => {
      try {
        await api.post('/pomodoro-session/heartbeat', {
          deviceId,
          sessionId,
          mode,
          boundTaskId: boundTask.id,
          boundTaskTitle: boundTask.title,
          boundGuideSlug: boundTask.guideSlug ?? null,
          running,
          remainingSec: remainingSecRef.current,
        });
      } catch (e: any) {
        if (e?.response?.status === 409) {
          // 被抢占：停止并清理本地会话
          sessionIdRef.current = null;
          resetAfterStolen();
          window.alert('番茄时钟已被另一台设备抢占，本设备已停止计时。');
        }
      }
    };

    void tick();
    heartbeatTimerRef.current = window.setInterval(() => void tick(), 12_000);
    return () => {
      if (heartbeatTimerRef.current) window.clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    };
  }, [isLoggedIn, running, sessionActive, boundTask?.id, boundTask?.title, mode]);


  const completeOrSkipBreak = () => {
    if (!boundTask) return;
    const eff = resolveEffective(boundTask, settings);
    if (!eff.enabled) return;
    if (mode === 'focus') {
      void ensureAudioUnlocked().then(() => void playBeep('focus_end'));
      setCounts((prev) => {
        const dk = getPomodoroDayKey();
        const next = { ...prev, [dk]: (prev[dk] ?? 0) + 1 };
        savePomodoroCounts(next);
        return next;
      });
      setMode('break');
      setRemainingSec(eff.breakMinutes * 60);
      setRunning(true);
      setSessionActive(true);
      return;
    }
    // break：结束休息，回到专注并暂停
    void ensureAudioUnlocked().then(() => void playBeep('break_end'));
    setMode('focus');
    setRemainingSec(eff.focusMinutes * 60);
    setRunning(false);
    setSessionActive(true);
  };

  // 设置变更：持久化，并在未运行时校准剩余时间
  useEffect(() => {
    savePomodoroSettings(settings);
    setRemainingSec((prev) => {
      if (running) return prev;
      const eff = resolveEffective(boundTask, settings);
      const secs = (mode === 'focus' ? eff.focusMinutes : eff.breakMinutes) * 60;
      return secs;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  // 设置变更：登录态下自动同步到云端（debounce）
  useEffect(() => {
    if (!isLoggedIn) return;
    if (pushTimerRef.current) window.clearTimeout(pushTimerRef.current);
    pushTimerRef.current = window.setTimeout(async () => {
      try {
        const res = await api.put<{ updatedAt: string }>('/pomodoro-settings', {
          data: settings,
          baseUpdatedAt: cloudUpdatedAtRef.current ?? undefined,
        });
        cloudUpdatedAtRef.current = res.data?.updatedAt ?? cloudUpdatedAtRef.current;
      } catch {
        // 冲突/网络：先不打断用户；后续可补“冲突提示 + 以云端为主”策略
      }
    }, 650);
    return () => {
      if (pushTimerRef.current) window.clearTimeout(pushTimerRef.current);
    };
  }, [settings, isLoggedIn]);

  // 计时循环
  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(() => setRemainingSec((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, [running]);

  // 到点处理
  useEffect(() => {
    if (!running) return;
    if (remainingSec > 0) return;
    const eff = resolveEffective(boundTask, settings);

    // 专注结束：今日番茄 +1，自动进入休息并继续跑
    if (mode === 'focus') {
      void playBeep('focus_end');
      setCounts((prev) => {
        const dk = getPomodoroDayKey();
        const next = { ...prev, [dk]: (prev[dk] ?? 0) + 1 };
        savePomodoroCounts(next);
        return next;
      });
      setMode('break');
      setRemainingSec(eff.breakMinutes * 60);
      setSessionActive(true);
      return;
    }

    // 休息结束：回到专注，但默认暂停
    void playBeep('break_end');
    setMode('focus');
    setRemainingSec(eff.focusMinutes * 60);
    setRunning(false);
    setSessionActive(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingSec, running, mode]);

  // 防误刷新/关闭：运行中拦截
  useEffect(() => {
    if (!running) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const k = (e.key || '').toLowerCase();
      const isRefresh = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && k === 'r');
      if (!isRefresh) return;
      e.preventDefault();
      const ok = window.confirm('番茄时钟正在运行。刷新会中断计时，确定要刷新吗？');
      if (ok) window.location.reload();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [running]);

  // 防误跳转：专注屏中拦截站内路由变更（点击导航 / navigate / 前进后退）
  // 说明：番茄已支持“离开 Today 仍持续”，所以不再因为 running 就阻塞全站导航；仅在沉浸专注屏时拦截。
  const navigationContext = useContext(UNSAFE_NavigationContext) as any;
  useEffect(() => {
    const navigator = navigationContext?.navigator as any;
    if (!focusScreen) return;
    if (!navigator?.block) return;

    const unblock = navigator.block((tx: any) => {
      const ok = window.confirm('你正在专注屏中。离开会退出专注屏，确定要离开吗？');
      if (!ok) return;
      setFocusScreen(false);
      try {
        if (document.fullscreenElement) void document.exitFullscreen?.();
      } catch {
        // ignore
      }
      unblock();
      tx.retry();
    });

    return unblock;
  }, [focusScreen, navigationContext]);

  const value: PomodoroContextValue = {
    settings,
    effective,
    mode,
    running,
    sessionActive,
    remainingSec,
    todayPomodoros,
    boundTask,

    showSettings,
    focusScreen,
    focusShowGuide,

    setSettings,
    setShowSettings,
    setFocusScreen,
    setFocusShowGuide,

    bindToTask,
    toggleRunning,
    reset,
    completeOrSkipBreak,
  };

  return <PomodoroContext.Provider value={value}>{children}</PomodoroContext.Provider>;
};

export function usePomodoro() {
  const ctx = useContext(PomodoroContext);
  if (!ctx) throw new Error('usePomodoro must be used within PomodoroProvider');
  return ctx;
}

